'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { ResponsiveContainer, AreaChart, XAxis, YAxis, Tooltip, type TooltipProps, Area } from 'recharts';
import { DateTimeSDK, std } from '@funkey/shared/utils';
import { InputSelect } from '~/components/primitives';
import { graphql } from '@funkey/types/graphql/gql';
import type { I18N_CONF } from '@funkey/locales/config';
import type { I18n } from '@funkey/locales';
import type { db } from '@funkey/api/src';
import { api } from '~/modules';
import { curryProps, toStateObject } from '~/utils/react';
import Title from '~/components/utils/Title';
import { useI18n } from '~/features/i18n';
import BizzLayout from '~/app/[locale]/bizz/[slug]/_components/BizzLayout';
import { parseAsStringEnum, useQueryState } from 'nuqs';
import { cx } from 'class-variance-authority';
import { BizzYearSelect } from './client';
import type { DataKey } from 'recharts/types/util/types';
import { DownloadButton } from '~/utils/files';
import type { Route } from 'next';
import { summariseDates, NUM_MONTHS, type PeriodCutoff } from './summariseDates';
import { aggregateMostBookedActivities } from './aggregateMostBookedActivities';
const COLORS = {
  TURQOISE_500: 'rgb(55 180 162)',
  BLUE_300: 'rgb(174, 188, 205)',
  BLUE_500: 'rgb(124 147 174)',
  BLUE: 'rgb(33 63 114)',
  TERTIARY: 'rgb(235 88 72)'
} as const;
const GRADIENT_NAME = 'dashboard_graph_gradient';

/**
 * Deal statuses that count as an actually-booked teambuilding for the
 * "Most booked" dashboard tab. Referenced by both the `teambuildings` query
 * (filters server-side) and the `most_booked` client-side aggregator.
 */
const BOOKED_DEAL_STATUSES = ['WON'] as const satisfies ReadonlyArray<typeof db.deals.$inferSelect.status>;
export const BizzDashboardGraph: React.FC<{
  bizz: Pick<typeof db.businesses.$inferSelect, 'id' | 'tenantId' | 'createdAt' | 'slug'>;
  year: number;
  locale: I18N_CONF.Locale;
}> = props => {
  const $ = _c(5);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = parseAsStringEnum(std.Object.keys(DATASETS)).withDefault("teambuildings");
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const dataset = toStateObject(useQueryState("chart", t0));
  const i18n = useI18n();
  const Component = DATASETS[dataset.value];
  let t1;
  if ($[1] !== Component || $[2] !== i18n || $[3] !== props) {
    t1 = <Component {...props} i18n={i18n} />;
    $[1] = Component;
    $[2] = i18n;
    $[3] = props;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
const BizzDashboardGraphLayout: React.FC<React.ComponentProps<typeof BizzDashboardGraph> & {
  graph: React.ReactNode;
  summary: React.ReactNode;
  isLoading: boolean;
  i18n: I18n;
}> = props => {
  const $ = _c(59);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = parseAsStringEnum(std.Object.keys(DATASETS)).withDefault("teambuildings");
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = useQueryState("chart", t0);
  let t2;
  if ($[1] !== t1) {
    t2 = toStateObject(t1);
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const dataset = t2;
  let t3;
  if ($[3] !== props.i18n) {
    t3 = props.i18n._("Statistics");
    $[3] = props.i18n;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <Title>{t3}</Title>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = `Funkey_dashboard_${props.bizz.slug}.xlsx`;
  const t6 = `/${props.locale}/bizz/${props.bizz.slug}/dashboard/xlsx` satisfies Route;
  let t7;
  if ($[7] !== t5 || $[8] !== t6) {
    t7 = {
      name: t5,
      url: t6
    };
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== props.i18n) {
    t8 = props.i18n._("Export");
    $[10] = props.i18n;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t8) {
    t9 = <span className="sr-only">{t8}</span>;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t7 || $[15] !== t9) {
    t10 = <DownloadButton variant="ghost" className="h-fit" file={t7}>{t9}</DownloadButton>;
    $[14] = t7;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t4) {
    t11 = <div className="flex flex-wrap items-center justify-between gap-3">{t4}{t10}</div>;
    $[17] = t10;
    $[18] = t4;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== dataset) {
    t12 = newDataset => dataset.setValue(newDataset as typeof dataset.value);
    $[20] = dataset;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const t13 = dataset.value;
  let t14;
  if ($[22] !== props.i18n) {
    t14 = props.i18n._("Teambuildings");
    $[22] = props.i18n;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t14) {
    t15 = {
      value: "teambuildings",
      label: t14
    };
    $[24] = t14;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== props.i18n) {
    t16 = props.i18n._("Requests");
    $[26] = props.i18n;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== t16) {
    t17 = {
      value: "requests",
      label: t16
    };
    $[28] = t16;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== props.i18n) {
    t18 = props.i18n._("Most booked");
    $[30] = props.i18n;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== t18) {
    t19 = {
      value: "most_booked",
      label: t18
    };
    $[32] = t18;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== t15 || $[35] !== t17 || $[36] !== t19) {
    t20 = [t15, t17, t19];
    $[34] = t15;
    $[35] = t17;
    $[36] = t19;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== dataset.value || $[39] !== t12 || $[40] !== t20) {
    t21 = <InputSelect className="w-full" onChange={t12} value={t13} options={t20} />;
    $[38] = dataset.value;
    $[39] = t12;
    $[40] = t20;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== props.bizz.createdAt) {
    t22 = <BizzYearSelect bizzCreatedAt={props.bizz.createdAt} />;
    $[42] = props.bizz.createdAt;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== t21 || $[45] !== t22) {
    t23 = <menu className="flex gap-3">{t21}{t22}</menu>;
    $[44] = t21;
    $[45] = t22;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== props.summary || $[48] !== t11 || $[49] !== t23) {
    t24 = <BizzLayout.Section className="flex w-[410px] flex-col gap-12 border-r">{t11}{t23}{props.summary}</BizzLayout.Section>;
    $[47] = props.summary;
    $[48] = t11;
    $[49] = t23;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  const t25 = props.isLoading && "animate-pulse";
  let t26;
  if ($[51] !== t25) {
    t26 = cx("pb-4! pl-0! w-full", t25);
    $[51] = t25;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  let t27;
  if ($[53] !== props.graph || $[54] !== t26) {
    t27 = <BizzLayout.Section className={t26}>{props.graph}</BizzLayout.Section>;
    $[53] = props.graph;
    $[54] = t26;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  let t28;
  if ($[56] !== t24 || $[57] !== t27) {
    t28 = <div className="w-full md:flex">{t24}{t27}</div>;
    $[56] = t24;
    $[57] = t27;
    $[58] = t28;
  } else {
    t28 = $[58];
  }
  return t28;
};
const DATASETS = {
  teambuildings: props => {
    const query = api.useGraphqlQuery(graphql(`
                query BizzDashboardGraphTeambuildings(
                    $bizzID: Int!
                    $dealDateRange: DateRange!
                    $status: String!
                ) {
                    business(id: $bizzID) {
                        deals(startsAt: $dealDateRange, status: $status) {
                            startsAt
                        }
                    }
                }
            `), {
      bizzID: props.bizz.id,
      status: BOOKED_DEAL_STATUSES[0],
      dealDateRange: {
        from: DateTimeSDK.BRUDate.beginOfYear(props.year - 1),
        to: DateTimeSDK.BRUDate.endOfYear(props.year)
      }
    }, {
      placeholderData: undefined
    });
    const dealCounts = summariseDates(query.data?.business?.deals.map(x => new Date(x.startsAt)) ?? [], props.year);
    return <BizzDashboardGraphLayout {...props} isLoading={query.isLoading} summary={<Summary currentYear={props.year} i18n={props.i18n} total={query.isLoading ? null : dealCounts.thisYear} previousYearTotal={query.isLoading ? null : dealCounts.prevYear} unitRenderer={value => props.i18n._('{amount} teambuildings', {
      amount: value
    })} />} graph={<GraphAreaChart data={dealCounts?.thisYearByMonth.map((nDeals, monthIdx) => ({
      nDeals,
      monthIdx
    })) ?? []} graphColor={COLORS.TURQOISE_500} tooltipValueRenderer={value => props.i18n._('{amount} teambuildings', {
      amount: value
    })} xAxisDataKey={entry => displayMonth(entry.monthIdx, props.i18n.locale, 'short')} yAxisDataKey="nDeals" />} />;
  },
  requests: props => {
    /**
     * Tim/FUN-4401: requests are counted by DEAL CREATION DATE (not start date),
     * and when the selected year is the current calendar year we compare
     * "YTD this year" vs "same period last year" so the user can tell whether
     * requests are running ahead of or behind the previous year.
     *
     * `todayBRU` is memoised on `props.year` so the Date reference is stable across
     * renders. Without this, every render creates a new Date -> new GraphQL
     * variables object -> new react-query key -> refetch -> infinite loop.
     *
     * Brussels is used as the single reporting clock here so the YTD cutoff and
     * the query's `toDate` agree for viewers outside CET/CEST. See summariseDates.ts.
     */
    const {
      periodCutoff,
      toDate
    } = useMemo(() => {
      const todayBRU = DateTimeSDK.bruDate();
      const isCurrentYear = todayBRU.getFullYear() === props.year;
      return {
        periodCutoff: isCurrentYear ? {
          month: todayBRU.getMonth(),
          day: todayBRU.getDate()
        } satisfies PeriodCutoff : undefined,
        toDate: isCurrentYear ? todayBRU : DateTimeSDK.BRUDate.endOfYear(props.year)
      };
    }, [props.year]);
    const query = api.useGraphqlQuery(graphql(`
                query BizzDashboardGraphRequests($bizzID: Int!, $dealDateRange: DateRange!) {
                    business(id: $bizzID) {
                        deals(createdAt: $dealDateRange) {
                            createdAt
                        }
                    }
                }
            `), {
      bizzID: props.bizz.id,
      dealDateRange: {
        /*
         * Fetch both the selected year and the previous year so we can
         * compute a year-on-year comparison client-side.
         */
        from: DateTimeSDK.BRUDate.beginOfYear(props.year - 1),
        to: toDate
      }
    }, {
      placeholderData: undefined
    });
    const dealCounts = summariseDates(query.data?.business?.deals?.map(x => new Date(x.createdAt)) ?? [], props.year, periodCutoff);
    return <BizzDashboardGraphLayout {...props} isLoading={query.isLoading} summary={<Summary currentYear={props.year} i18n={props.i18n} total={query.isLoading ? null : dealCounts.thisYear} previousYearTotal={query.isLoading ? null : dealCounts.prevYear} monthsInPeriod={dealCounts.monthsInPeriod} unitRenderer={value => props.i18n._('{amount} requests', {
      amount: value
    })} />} graph={<GraphAreaChart data={dealCounts?.thisYearByMonth.map((nDeals, monthIdx) => ({
      nDeals,
      monthIdx
    })) ?? []} graphColor={COLORS.BLUE} tooltipValueRenderer={value => props.i18n._('{amount} requests', {
      amount: value
    })} xAxisDataKey={entry => displayMonth(entry.monthIdx, props.i18n.locale, 'short')} yAxisDataKey="nDeals" />} />;
  },
  most_booked: props => {
    const query = api.useGraphqlQuery(graphql(`
                query BizzDashboardGraphMostBooked(
                    $bizzID: Int!
                    $dealDateRange: DateRange!
                    $status: String!
                    $itemStatus: DealStatus!
                ) {
                    business(id: $bizzID) {
                        deals(startsAt: $dealDateRange, status: $status) {
                            id

                            items(status: $itemStatus) {
                                activityVariant {
                                    activity {
                                        id
                                        name
                                    }
                                }
                            }
                        }
                    }
                }
            `), {
      bizzID: props.bizz.id,
      status: BOOKED_DEAL_STATUSES[0],
      itemStatus: BOOKED_DEAL_STATUSES[0],
      dealDateRange: {
        from: DateTimeSDK.BRUDate.beginOfYear(props.year),
        to: DateTimeSDK.BRUDate.endOfYear(props.year)
      }
    }, {
      placeholderData: undefined
    });
    const activities = query.data?.business ? aggregateMostBookedActivities(query.data.business.deals) : null;
    const maxCount = activities?.[0]?.count ?? 0;
    return <BizzDashboardGraphLayout {...props} isLoading={query.isLoading} summary={<p className="w-full">
                        {props.i18n._('bizz.dashboard.graph.most_booked.description')}
                    </p>} graph={query.isLoading ? <MostBookedSkeleton /> : activities === null ? null : activities.length === 0 ? <div className="flex min-h-40 items-center justify-center text-center">
                            {props.i18n._('No activities found')}
                        </div> : <div className="flex flex-col gap-4 px-6 py-4">
                            {activities.map((activity, i) => <div key={activity.id} className="flex flex-col gap-1">
                                    <div className="flex items-baseline justify-between gap-4">
                                        <span className="truncate text-sm font-medium">
                                            {activity.name}
                                        </span>
                                        <span className="shrink-0 text-sm font-semibold tabular-nums">
                                            {activity.count}
                                        </span>
                                    </div>
                                    <div className="h-2.5 w-full overflow-hidden rounded-full bg-blue-50">
                                        <div className="pointer-events-none h-full w-0 animate-bar-fill rounded-full bg-secondary" style={{
            '--bar-width': `${Math.max(activity.count / Math.max(maxCount, 1) * 100, 4)}%`,
            animationDelay: `${i * 60}ms`
          } as React.CSSProperties} />
                                    </div>
                                </div>)}
                        </div>} />;
  }
} as const satisfies Record<string, (props: Pick<React.ComponentProps<typeof BizzDashboardGraph>, 'bizz' | 'locale'> & {
  i18n: ReturnType<typeof useI18n>;
  year: number;
}) => React.ReactNode>;
const SKELETON_BAR_WIDTHS = [90, 78, 68, 58, 48, 38, 28, 20] as const;
const MostBookedSkeleton: React.FC = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex flex-col gap-4 px-6 py-4">{SKELETON_BAR_WIDTHS.map(_temp)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const GraphAreaChart = props => {
  const $ = _c(23);
  let t0;
  if ($[0] !== props.graphColor) {
    t0 = createTransparantLinearGradient(GRADIENT_NAME, props.graphColor, {
      offsetPercentage: 5,
      opacity: 0.3,
      direction: "top-down"
    });
    $[0] = props.graphColor;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const GRADIENT = t0;
  const t1 = props.data;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      top: 5,
      right: 5,
      left: 0,
      bottom: 0
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = GRADIENT.domElement;
  let t4;
  if ($[3] !== props.xAxisDataKey) {
    t4 = <XAxis stroke={COLORS.BLUE_300} axisLine={false} tickLine={false} dataKey={props.xAxisDataKey} />;
    $[3] = props.xAxisDataKey;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== props.yAxisDataKey) {
    t5 = <YAxis stroke={COLORS.BLUE_300} tickLine={false} axisLine={false} dataKey={props.yAxisDataKey} />;
    $[5] = props.yAxisDataKey;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== props.graphColor || $[8] !== props.tooltipValueRenderer) {
    t6 = curryProps(CustomTooltip as any, {
      color: props.graphColor,
      valueRenderer: props.tooltipValueRenderer
    });
    $[7] = props.graphColor;
    $[8] = props.tooltipValueRenderer;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = t6 as any;
  let t8;
  if ($[10] !== t7) {
    t8 = <Tooltip content={t7} />;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== GRADIENT.colorURL || $[13] !== props.graphColor || $[14] !== props.yAxisDataKey) {
    t9 = <Area type="monotone" dataKey={props.yAxisDataKey} strokeWidth={4} stroke={props.graphColor} fill={GRADIENT.colorURL} />;
    $[12] = GRADIENT.colorURL;
    $[13] = props.graphColor;
    $[14] = props.yAxisDataKey;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== GRADIENT.domElement || $[17] !== props.data || $[18] !== t4 || $[19] !== t5 || $[20] !== t8 || $[21] !== t9) {
    t10 = <ResponsiveContainer width="100%" height="100%"><AreaChart data={t1} margin={t2}>{t3}{t4}{t5}{t8}{t9}</AreaChart></ResponsiveContainer>;
    $[16] = GRADIENT.domElement;
    $[17] = props.data;
    $[18] = t4;
    $[19] = t5;
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
};
const Summary: React.FC<{
  i18n: I18n;
  total: number | null;
  previousYearTotal: number | null;

  /**
   * Number of months the `total` covers. Defaults to 12 (full year).
   * When comparing YTD vs same-period-last-year, pass the number of elapsed
   * months so "Avg. per month" reflects the counted period instead of /12.
   */
  monthsInPeriod?: number;
  unitRenderer?: (value: string | number) => React.ReactNode;
  showComparison?: boolean;
  currentYear: number;
}> = props => {
  const $ = _c(4);
  const delta = props.total !== null && props.previousYearTotal !== null ? props.total - props.previousYearTotal : null;
  const months = props.monthsInPeriod ?? NUM_MONTHS;
  let t0;
  if ($[0] !== delta || $[1] !== months || $[2] !== props) {
    t0 = <ul className="grid gap-6">{([[props.i18n._("Year total"), props.total ?? "-"], [props.i18n._("Avg. per month"), props.total !== null && months > 0 ? Math.round(props.total / months) : "-"], ...(props.showComparison === false ? [] : [[props.i18n._("Compared to {year}", {
        year: props.currentYear - 1
      }), delta !== null ? `${delta > 0 ? "+" : ""}${delta}` : "-"] as const])] as const).map(t1 => {
        const [label, value] = t1;
        return <li key={label} className="flex items-center justify-between"><span>{label}</span><span>{props.unitRenderer ? props.unitRenderer(value) : value}</span></li>;
      })}</ul>;
    $[0] = delta;
    $[1] = months;
    $[2] = props;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
};
export const CustomTooltip: React.FC<TooltipProps<number, 'nDeals'> & {
  color: string;
  valueRenderer: (value: number | string) => React.ReactNode;
}> = props => {
  const $ = _c(19);
  if (!props.active || !props.payload?.length) {
    return null;
  }
  let t0;
  let t1;
  let t2;
  let t3;
  if ($[0] !== props.color || $[1] !== props.label || $[2] !== props.payload || $[3] !== props.valueRenderer) {
    const value = props.payload.find(_temp2)?.value ?? 0;
    t1 = "flex flex-col text-center";
    if ($[8] !== props.color) {
      t2 = {
        color: props.color
      };
      $[8] = props.color;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    if ($[10] !== props.label) {
      t3 = <h3 className="font-bold">{props.label}</h3>;
      $[10] = props.label;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    t0 = props.valueRenderer(value);
    $[0] = props.color;
    $[1] = props.label;
    $[2] = props.payload;
    $[3] = props.valueRenderer;
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t0 = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
  }
  let t4;
  if ($[12] !== t0) {
    t4 = <p>{t0}</p>;
    $[12] = t0;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t1 || $[15] !== t2 || $[16] !== t3 || $[17] !== t4) {
    t5 = <div className={t1} style={t2}>{t3}{t4}</div>;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return t5;
};
function displayMonth(monthIdx: number, locale: I18N_CONF.Locale, formatMonth: NonNullable<Intl.DateTimeFormatOptions['month']>) {
  return new Date(2000, monthIdx, 1).toLocaleString(locale, {
    month: formatMonth
  });
}
function createTransparantLinearGradient(id: string, color: string, options: {
  direction: 'right-left' | 'top-down';
  opacity?: number;
  offsetPercentage?: number;
}) {
  const domElement = <defs className="">
            <linearGradient id={id} gradientTransform={options.direction === 'top-down' ? `rotate(90)` : undefined}>
                <stop offset={`${options.offsetPercentage ?? 5}%`} stopColor={options.direction === 'top-down' ? color : '#FFF'} stopOpacity={options.direction === 'top-down' ? options.opacity ?? 0.3 : 0} />
                <stop offset="99%" stopColor={options.direction === 'top-down' ? '#FFF' : color} stopOpacity={options.direction === 'top-down' ? 0 : options.opacity ?? 0.3} />
            </linearGradient>
        </defs>;
  return {
    colorURL: `url(#${id})`,
    /** Needs to be rendered in the dom so that the `colorURL` can point to it */
    domElement
  };
}
function _temp(width, i) {
  return <div key={i} className="flex flex-col gap-1"><div className="flex h-5 items-center justify-between gap-4"><span className="h-3 w-32 animate-pulse rounded bg-blue-100" /><span className="h-3 w-6 animate-pulse rounded bg-blue-100" /></div><div className="h-2.5 w-full overflow-hidden rounded-full bg-blue-50"><div className="h-full animate-pulse rounded-full bg-blue-100" style={{
        width: `${width}%`
      }} /></div></div>;
}
function _temp2(x) {
  return x.name === "nDeals";
}